import { useQuery } from "@tanstack/react-query"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { CatalogSubscriptionDetails } from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.types"

import { CATALOG_ENDPOINTS } from "@endpoints/catalogs"

import { CatalogCategoryTab } from "./BrandItems.types"

export function useCategoriesQuery(catalogId: string, brandParam: string) {
    const { user } = useUser()
    const [categoriesEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.CATEGORIES])
    // The brandless bucket is addressed by the null-scope sentinel (brand=0), so one endpoint
    // serves both a real brand's tabs and the "No Brand" page's.
    const categoriesEndpoint = `${categoriesEndpointTemplate}?catalog=${catalogId}&brand=${brandParam}`

    return useQuery({
        queryKey: ["catalog-categories", catalogId, brandParam, categoriesEndpoint],
        queryFn: async () => {
            const response = await fetch(categoriesEndpoint)

            if (!response.ok) {
                throw new Error(`Request to ${categoriesEndpoint} failed with status ${response.status}.`)
            }

            return (await response.json()) as CatalogCategoryTab[]
        },
        enabled: !!user?.service_company?.slug,
    })
}

// Shares the settings page's ["catalog-subscriptions", catalogId, …] cache so the two pages resolve
// item coverage off one subscriptions listing rather than fetching it twice; the key must stay in
// lockstep with useCatalogSubscriptionsQuery for that sharing to hold.
export function useBrandItemsSubscriptionsQuery(catalogId: number) {
    const { user } = useUser()
    const [subscriptionsEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTIONS])
    const subscriptionsEndpoint = `${subscriptionsEndpointTemplate}?catalog=${catalogId}`

    return useQuery({
        queryKey: ["catalog-subscriptions", catalogId, subscriptionsEndpoint],
        queryFn: async () => {
            const response = await fetch(subscriptionsEndpoint)

            if (!response.ok) {
                throw new Error(`Request to ${subscriptionsEndpoint} failed with status ${response.status}.`)
            }

            return (await response.json()) as CatalogSubscriptionDetails[]
        },
        enabled: !!user?.service_company?.slug,
    })
}
